import { useState, useEffect } from 'react';

export default function PaymentHistoryTable() {
  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState("");

  const API_URL = process.env.REACT_APP_API_URL || "https://didrouteapi.com/api";

  useEffect(() => {
    fetchTransactions();
  }, [currentPage]);

  const fetchTransactions = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem("token");
      
      if (!token) {
        setError("No authentication token found. Please login again.");
        return;
      }
      
      const response = await fetch(
        `${API_URL}/wallet/transactions?page=${currentPage}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        setTransactions(data.transactions);
        setTotalPages(data.totalPages);
      } else {
        throw new Error(data.error || "Failed to fetch transactions");
      }
    } catch (error) {
      setError(error.message || "Failed to fetch transactions");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  return (
    <div className="bg-white p-2 rounded-lg shadow-lg">
      <div className="mx-auto">
        <div className="bg-white py-10">
          <div className="px-4 sm:px-4 lg:px-8">
            <div className="sm:flex sm:items-center">
              <div className="sm:flex-auto">
                <h1 className="text-xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
                  Payment History
                </h1>
              </div>
            </div>

            {error && <div className="mt-4 text-red-600">{error}</div>}

            {isLoading ? (
              <div className="mt-4 text-center">Loading...</div>
            ) : (
              <div className="mt-8 flow-root">
                <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8 custom-scrollbar">
                  <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                    <table className="min-w-full divide-y divide-gray-700">
                      <thead>
                        <tr>
                          <th className="py-3.5 pl-4 pr-3 text-left text-xs font-medium text-black sm:pl-0">
                            Previous Balance
                          </th>
                          <th className="px-3 py-3.5 text-left text-xs font-medium text-black">
                            Amount
                          </th>
                          <th className="px-3 py-3.5 text-left text-xs font-medium text-black">
                            New Balance
                          </th>
                          <th className="px-3 py-3.5 text-left text-xs font-medium text-black">
                            Type
                          </th>
                          <th className="px-3 py-3.5 text-left text-xs font-medium text-black">
                            Description
                          </th>
                          <th className="px-3 py-3.5 text-left text-xs font-medium text-black">
                            Date
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {transactions.length > 0 ? (
                          transactions.map((transaction, index) => {
                            // Parse values to ensure they're numbers
                            const prevBalance = parseFloat(transaction.prev_balance);
                            const amount = parseFloat(transaction.amount);
                            
                            // Calculate new balance based on transaction type
                            let newBalance;
                            if (transaction.transaction_type === "CREDIT") {
                              newBalance = prevBalance + amount;
                            } else if (transaction.transaction_type === "DEBIT") {
                              newBalance = prevBalance - amount;
                            } else {
                              newBalance = prevBalance; // Fallback
                            }
                            
                            // For the display, let's adjust the "Amount" column to show action
                            const amountDisplay = transaction.transaction_type === "DEBIT" 
                              ? `-$${amount.toFixed(2)}` 
                              : `+$${amount.toFixed(2)}`;
                            
                            return (
                              <tr key={index}>
                                <td className="whitespace-nowrap py-4 pl-4 pr-3 text-xs text-gray-900 sm:pl-0">
                                  ${prevBalance.toFixed(2)}
                                </td>
                                <td className={`whitespace-nowrap px-3 py-4 text-xs ${
                                  transaction.transaction_type === "CREDIT"
                                    ? "text-green-600"
                                    : "text-red-600"
                                }`}>
                                  {amountDisplay}
                                </td>
                                <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-900">
                                  ${newBalance.toFixed(2)}
                                </td>
                                <td className={`whitespace-nowrap px-3 py-4 text-xs ${
                                  transaction.transaction_type === "CREDIT"
                                    ? "text-green-600"
                                    : "text-red-600"
                                }`}>
                                  {transaction.transaction_type}
                                </td>
                                <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-900">
                                  {transaction.description}
                                </td>
                                <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-900">
                                  {new Date(transaction.created_at).toLocaleString()}
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan="6" className="text-center py-4 text-gray-500">
                              No transactions found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between px-6 py-2">
          <div className="flex items-center text-sm text-gray-900">
            <span>Page {currentPage} of {totalPages}</span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`px-3 py-1 rounded ${
                currentPage === 1
                  ? "bg-gray-700 text-gray-400 cursor-not-allowed"
                  : "bg-blue-600 text-white hover:bg-blue-700"
              }`}
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`px-3 py-1 rounded ${
                currentPage === totalPages
                  ? "bg-gray-700 text-gray-400 cursor-not-allowed"
                  : "bg-blue-600 text-white hover:bg-blue-700"
              }`}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
